import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import PlaygroundBreadcrumbs from "../../components/common/playground-breadcrumbs";
import { startPreprocess } from "../../api/batches";
import JobListFilters from "../../components/job/list/filters";
import { useAuthStore } from "../../authProvider";
import BulkResultsDialog from "../../components/job/list/bulk-results-dialog";
import AdvancedStatsBox from "../../components/job/list/advanced-stats";
import ExportDialog from "../../components/job/list/export";
import { JobListStatusBanner } from "../../components/job/list/job-list-status-banner";
import { JobListHeader } from "../../components/job/list/job-list-header";
import { JobListContent } from "../../components/job/list/job-list-content";
import { JobListActionMenu } from "../../components/job/list/job-list-action-menu";
import { useGlobalJobListData } from "../../components/job/list/hooks/use-global-job-list-data";
import { LoadingButton } from "@mui/lab";
import { UploadImages } from "../../components/job/list/upload-images";
import KeywordSearch from "../../components/job/list/keyword-search";
import { BatchConfiguration } from "../../components/job/list/batch-configuration";

export const JobList: React.FC = () => {
  const { user } = useAuthStore();

  const [advancedStatsDialog, setAdvancedStatsDialog] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showClientImageUpload, setShowClientImageUpload] = useState(false);
  const [showReconfiguration, setShowReconfiguration] = useState(false);

  const { batchId } = useParams();

  const {
    // job
    brand,
    batch,
    jobs,
    isLoadingBatch,
    isLoadingBrand,
    isLoadingJobs,
    isFetchingNextPage,
    refetch,
    refetchBatch,
    observerTarget,
    // sheet
    fetchSheetData,
    fetchingSheetData,
    sheet,
    sheetTotalItems,
    // stats
    stats,
    advancedStats,
    reloadStats,
    // derived stats
    derivedStats,
    // filters
    statusFilter,
    columnFilter,
    handleStatusFilterChange,
    handleColumnFilterChange,
    // job grouping
    filteredAndGroupedJobs,
    // polling
    isPolling,
    startPolling,
    // bulk actions
    showBulkActions,
    selectedJobs,
    bulkLoading,
    bulkResults,
    bulkResultsDialogOpen,
    handleJobSelectionChange,
    handleSelectAll,
    handleClearSelection,
    handleBulkStatusChange,
    toggleBulkActions,
    closeBulkResultsDialog,
    // keyword search
    showKeywordSearch,
    toggleKeywordSearch,
    // vars
    configurationCompleted,
    canPreProcess,
    isCompleted,
    // translations
    hasPendingTranslations,
  } = useGlobalJobListData(batchId);

  const isMissingSheet = configurationCompleted && !fetchingSheetData && !sheet;

  useEffect(() => {
    if (isMissingSheet) {
      setShowReconfiguration(true);
    } else {
      setShowReconfiguration(false);
    }
  }, [isMissingSheet]);

  // Who can see and interact with batch configuration?
  // Based on the batch's organization AI permission - same for all users
  const canSeeBatchConfiguration = brand?.organization_mode_allow_ai_generation === true;

  const isLoading = isLoadingBatch || isLoadingBrand || isLoadingJobs;

  const needsSetup =
    !isLoading && (!configurationCompleted || showReconfiguration);

  const showMainContent =
    !isLoading &&
    configurationCompleted &&
    !showReconfiguration &&
    brand &&
    batch &&
    jobs &&
    stats;

  return (
    <>
      <Dialog
        open={advancedStatsDialog}
        onClose={() => setAdvancedStatsDialog(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Statistics</DialogTitle>
        <DialogContent>
          {advancedStats && <AdvancedStatsBox stats={advancedStats} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvancedStatsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <BulkResultsDialog
        open={bulkResultsDialogOpen}
        onClose={closeBulkResultsDialog}
        results={bulkResults}
      />

      {showMainContent &&
        (user?.isManager || user?.isAdmin || user?.isClientRelated) && (
          <ExportDialog
            open={exportDialogOpen}
            onClose={() => setExportDialogOpen(false)}
            brandName={brand.name}
            batch={batch}
            stats={stats}
            hasPendingTranslations={hasPendingTranslations}
          />
        )}

      <Container
        maxWidth="lg"
        sx={{
          flex: 1,
          display: "flex",
          p: 4,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            width: "100%",
          }}
        >
          <PlaygroundBreadcrumbs
            brandName={brand?.name}
            brandId={brand?.id}
            batchName={batch?.name}
            batchId={batch?.id}
          />

          {(isLoading || fetchingSheetData) && (
            <Typography variant="body1">
              Loading batch data...
              <LoadingButton loading />
            </Typography>
          )}

          {!showMainContent && needsSetup && (
            <BatchConfiguration
              batchId={String(batch?.id)}
              hasGdriveFolder={!!batch?.gdrive_folder_id}
              existingMappings={batch?.settings?.custom_bullet_points || []}
              sheetData={sheet}
              canSeeBatchConfiguration={!!canSeeBatchConfiguration}
              isFetchingSheetData={fetchingSheetData}
              isReconfiguring={showReconfiguration}
              onComplete={async () => {
                setShowReconfiguration(false);
                await refetchBatch();
                await reloadStats();
                await refetch();
                await startPolling();
              }}
              onSheetUploaded={async () => {
                await refetchBatch();
                await fetchSheetData();
              }}
              onCancel={() => setShowReconfiguration(false)}
            />
          )}

          {showMainContent && (
            <>
              {jobs && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    width: "100%",
                  }}
                >
                  <JobListHeader
                    brand={brand}
                    batch={batch}
                    stats={stats}
                    advancedStats={advancedStats}
                    sheet={sheet}
                    user={user}
                    isPolling={isPolling}
                    canPreProcess={canPreProcess}
                    isCompleted={isCompleted}
                    showBulkActions={showBulkActions}
                    onToggleBulkActions={toggleBulkActions}
                    onStartPreprocess={async () => {
                      await startPreprocess({
                        batchId: String(batch?.id),
                      });

                      await startPolling();
                    }}
                    onOpenMenu={(event) => setMenuAnchorEl(event.currentTarget)}
                    showUploadImagesButton={!batch.gdrive_folder_id || false}
                    showClientImageUpload={showClientImageUpload}
                    setShowClientImageUpload={setShowClientImageUpload}
                    showKeywordSearch={showKeywordSearch}
                    onToggleKeywordSearch={toggleKeywordSearch}
                  />

                  <JobListActionMenu
                    anchorEl={menuAnchorEl}
                    batch={batch}
                    brand={brand}
                    advancedStats={advancedStats}
                    sheet={sheet}
                    user={user}
                    isPolling={isPolling}
                    isCompleted={isCompleted}
                    showKeywordSearch={showKeywordSearch}
                    onOpenAdvancedStats={() => {
                      setAdvancedStatsDialog(true);
                    }}
                    onOpenExport={() => {
                      setExportDialogOpen(true);
                    }}
                    onToggleKeywordSearch={toggleKeywordSearch}
                    onResetupBatch={() => setShowReconfiguration(true)}
                    open={Boolean(menuAnchorEl)}
                    onClose={() => setMenuAnchorEl(null)}
                    canSeeBatchConfiguration={canSeeBatchConfiguration}
                  />

                  {showClientImageUpload && (
                    <UploadImages
                      batchId={String(batch.id)}
                      stats={stats}
                      jobs={jobs || []}
                      onUploadComplete={async () => {
                        await refetch();
                        await refetchBatch();
                        await reloadStats();
                      }}
                    />
                  )}

                  {showKeywordSearch ? (
                    <KeywordSearch
                      batchId={String(batch.id)}
                      batchSettings={batch.settings}
                    />
                  ) : (
                    <>
                      {(isCompleted || !batch?.gdrive_folder_id) && (
                        <>
                          {sheet && (
                            <JobListFilters
                              statusFilter={statusFilter}
                              setStatusFilter={handleStatusFilterChange}
                              columnFilter={columnFilter}
                              setColumnFilter={handleColumnFilterChange}
                              totalJobs={stats?.total_jobs || 0}
                              jobsByStatus={stats?.jobs_by_status}
                              sheetData={sheet}
                              isAIEnabled={brand?.organization_mode_allow_ai_generation === true}
                            />
                          )}

                          {!user?.isClientRelated && stats?.copywriters && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: 1,
                              }}
                            >
                              {stats?.copywriters?.editors.length > 0 && (
                                <Typography variant="body2">
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    display="inline"
                                  >
                                    Copywriters:
                                  </Typography>{" "}
                                  {stats.copywriters.editors
                                    .map(
                                      (editor) =>
                                        `${editor.name} ${editor.surname}`
                                    )
                                    .join(", ")}
                                </Typography>
                              )}

                              {stats?.copywriters?.reviewers.length > 0 && (
                                <Typography variant="body2">
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    display="inline"
                                  >
                                    Reviewers:
                                  </Typography>{" "}
                                  {stats.copywriters.reviewers
                                    .map(
                                      (reviewer) =>
                                        `${reviewer.name} ${reviewer.surname}`
                                    )
                                    .join(", ")}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </>
                      )}

                      <JobListStatusBanner
                        hasGdriveFolder={!!batch.gdrive_folder_id}
                        isPolling={isPolling}
                        canPreProcess={canPreProcess}
                        isCompleted={isCompleted}
                        jobsThatCanBeWorked={derivedStats.jobsThatCanBeWorked}
                        sheetTotalItems={sheetTotalItems}
                        atLeastOneJobMissingImages={
                          derivedStats.atLeastOneJobMissingImages
                        }
                      />

                      <JobListContent
                        jobs={filteredAndGroupedJobs.jobs}
                        groupedJobs={filteredAndGroupedJobs.groups}
                        showBulkActions={showBulkActions}
                        selectedJobs={selectedJobs}
                        onJobSelectionChange={handleJobSelectionChange}
                        onSelectAll={(selected) =>
                          handleSelectAll(
                            selected,
                            filteredAndGroupedJobs.jobs.map((job) => job.id)
                          )
                        }
                        columnFilter={columnFilter}
                        statusFilter={statusFilter}
                        observerTarget={observerTarget}
                        isCompleted={isCompleted}
                        isFetchingNextPage={isFetchingNextPage}
                        onBulkStatusChange={handleBulkStatusChange}
                        onClearSelection={handleClearSelection}
                        bulkLoading={bulkLoading}
                        hasGdriveFolder={!!batch.gdrive_folder_id}
                        user={user}
                        batchSettings={batch.settings}
                        laraLanguages={batch.settings?.lara_languages}
                        laraInstructions={batch.settings?.lara_instructions}
                        laraStyle={batch.settings?.lara_style}
                      />
                    </>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Container>
    </>
  );
};
